<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useNumberInput } from '@/helpers/numberInput';
import { TippyDirective } from '@/helpers/tippy';

const vTippy = TippyDirective;

const props = withDefaults(
  defineProps<{
    modelValue?: number;
    min: number;
    max: number;
    step?: number;
    label: string;
    labelType?: 'static' | 'floating';
    leadingSymbol?: string;
    trailingSymbol?: string;
    errorMessage?: string;
    highlightError?: boolean;
    disabled?: boolean;
    tooltip?: string;
    hideRangeLabels?: boolean;
    hideVisualLabel?: boolean;
    allowOutOfRangeInput?: boolean;
  }>(),
  {
    modelValue: undefined,
    step: 1,
    labelType: 'static',
    leadingSymbol: undefined,
    trailingSymbol: undefined,
    errorMessage: undefined,
    highlightError: false,
    disabled: false,
    tooltip: undefined,
    hideRangeLabels: false,
    hideVisualLabel: false,
    allowOutOfRangeInput: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | undefined): void;
}>();

const allowNegative = ref(false);
const precision = ref(0);
const formatOnInput = ref(true);

const { formatValue, parseValue, handleBeforeInput } = useNumberInput({
  allowNegative,
  precision,
  formatOnInput,
});

const localValue = ref(props.modelValue ?? props.min);
const inputValue = ref(formatValue(props.modelValue));
let inputChanged = false;

function updateFromInput(value: string | undefined) {
  if (value === inputValue.value) return;
  inputValue.value = value ?? '';
  inputChanged = true;

  const parsed = value ? parseValue(value, true) : undefined;
  if (parsed !== undefined && !isNaN(parsed)) {
    localValue.value = props.allowOutOfRangeInput ? parsed : Math.min(Math.max(parsed, props.min), props.max);
    emit('update:modelValue', localValue.value);
  }
}

function handleInputFocusOut() {
  if (inputChanged && !isNaN(localValue.value)) {
    inputValue.value = formatValue(localValue.value);
    inputChanged = false;
  }
}

function updateFromSlider(event: Event) {
  const val = Number((event.target as HTMLInputElement).value);
  localValue.value = val;
  inputValue.value = formatValue(val);
  emit('update:modelValue', val);
}

watch(
  () => props.modelValue,
  (val) => {
    if (val !== localValue.value) {
      localValue.value = val ?? props.min;
      inputValue.value = formatValue(val);
      inputChanged = false;
    }
  },
);

const formatRangeLabel = (val: number) => {
  const formatted = val.toLocaleString();
  if (props.leadingSymbol) return `${props.leadingSymbol}${formatted}`;
  if (props.trailingSymbol) return `${formatted} ${props.trailingSymbol}`;
  return formatted;
};

const formattedMin = computed(() => formatRangeLabel(props.min));
const formattedMax = computed(() => formatRangeLabel(props.max));
</script>

<template>
  <div>
    <label
      v-if="labelType === 'static' && !hideVisualLabel"
      class="mb-2 block font-medium text-bridgit-navy"
      :for="String($attrs.id ?? 'slider-input')"
      :class="tooltip ? 'inline-flex items-center gap-1' : 'inline-block'"
    >
      {{ label }}
      <button
        v-if="tooltip"
        v-tippy="{ content: tooltip, trigger: 'mouseenter focus', placement: 'right' }"
        type="button"
        class="pointer-events-auto shrink-0 text-bridgit-lightGrey hover:text-bridgit-darkGrey"
        :aria-label="`${label} information`"
        @click.prevent
      >
        <BxIcon name="circleInfoLight" class="h-4 w-4" />
      </button>
    </label>
    <BxInput
      :id="String($attrs.id ?? 'slider-input')"
      :model-value="inputValue"
      :label="labelType === 'floating' ? (hideVisualLabel ? '' : label) : undefined"
      :label-type="labelType === 'floating' ? 'floating' : undefined"
      :aria-label="hideVisualLabel ? label : undefined"
      :tooltip="labelType === 'floating' && !hideVisualLabel ? tooltip : undefined"
      :leading-symbol="leadingSymbol"
      :error-message="errorMessage"
      :highlight-error="highlightError"
      :disabled="disabled"
      :data-test-id="`${$attrs.id ?? 'slider'}-range`"
      inputmode="numeric"
      :on-before-input="handleBeforeInput"
      @update:model-value="updateFromInput"
      @focusout="handleInputFocusOut"
    >
      <template v-if="trailingSymbol" #trailing>
        <span class="pr-1 text-gray-500">{{ trailingSymbol }}</span>
      </template>
    </BxInput>
    <div class="mt-3">
      <input
        type="range"
        :value="localValue"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        :aria-label="label"
        class="w-full accent-bridgit-navy"
        @input="updateFromSlider"
      />
      <div v-if="!hideRangeLabels" class="mt-1 flex justify-between text-sm text-gray-500">
        <span>{{ formattedMin }}</span>
        <span>{{ formattedMax }}</span>
      </div>
    </div>
  </div>
</template>
